import { useFormik } from "formik";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import { useState, useEffect, useRef } from "react";
import ComplaintService from "../../services/complaint/ComplaintService";
import { complaintmodel2 } from "../../models/complaints/complaint-model";
import AppLoading from "../app-loading/app-loading";
import { Image } from 'primereact/image';
import {ImageTemplateDemo} from "../images/file-upload-template";

interface UpdateProductComplaintProps {
  isUpdate: boolean;
  handleOnSuccess: (updatedComplaint: complaintmodel2) => void;
  updateComplaintInfo: complaintmodel2;
}

const UpdateProductComplaintDataTable: React.FC<UpdateProductComplaintProps> = ({ 
  isUpdate, 
  handleOnSuccess, 
  updateComplaintInfo,
}) => {
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState(true);
  const [loginUser, setLoginUser] = useState<any>();
  const [products, setProducts] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [updateImage,setUpdateImage] = useState<any>();
  const [containerType,setContainerType] = useState<string>("Complaints");

  interface UserDetail {
    userID: number;
    name: string;
    primaryContactNbr: string;
  }

  interface InvoiceProduct {
    productId: number;
    name: string;
    categoryID?: number;
    categoryName?: string;
  }

  interface UserInvoice {
    orderInvoiceID: number;
    invoiceNo: string;
  }

  useEffect(() => {
    const LoginDetails = localStorage.getItem('user');
    if (LoginDetails != null) {
      setLoginUser(JSON.parse(LoginDetails));
    }

    const loadInitialData = async () => {
      try {
        let userDetails: UserDetail[] = [];
        if (updateComplaintInfo.customerId) {
          userDetails = await ComplaintService.getAdminUsers(updateComplaintInfo.customerId.toString());
          const formattedUsers = userDetails.map((user: UserDetail) => ({
            userID: user.userID,
            detail: `${user.name}, ${user.primaryContactNbr}`,
            phoneNo: user.primaryContactNbr,
            name: user.name
          }));
          setUsers(formattedUsers);
        }

        let userInvoices: UserInvoice[] = [];
        if (updateComplaintInfo.customerId) {
          userInvoices = await ComplaintService.getUserInvoices(updateComplaintInfo.customerId);
          setInvoices(userInvoices);
        }

        let invoiceProducts: InvoiceProduct[] = [];
        if (updateComplaintInfo.invoiceId) {
          invoiceProducts = await ComplaintService.getInvoicedproducts(updateComplaintInfo.invoiceId);
          setProducts(invoiceProducts);
        }

        formik.setValues({
          id: updateComplaintInfo.id || 0,
          customerId: updateComplaintInfo.customerId || 0,
          invoiceId: updateComplaintInfo.invoiceId || 0,
          productId: updateComplaintInfo.productId || 0,
          categoryId: updateComplaintInfo.categoryId || 0,
          description: updateComplaintInfo.description || "",
          image: updateComplaintInfo.image || [],
          severity: updateComplaintInfo.severity || "",
          remarks: updateComplaintInfo.remarks || "",
          customerSupportAction: updateComplaintInfo.customerSupportAction || "",
          createdBy: updateComplaintInfo.createdBy || 0,
          createdDate: updateComplaintInfo.createdDate || "",
          modifiedBy: updateComplaintInfo.modifiedBy || 0,
          modifiedDate: updateComplaintInfo.modifiedDate || "",
          userName: updateComplaintInfo.customerName || userDetails.find((u: UserDetail) => u.userID === updateComplaintInfo.customerId)?.name || "",
          userNo: updateComplaintInfo.mobile?.toString() || "",
          productName: updateComplaintInfo.productName || invoiceProducts.find((p: InvoiceProduct) => p.productId === updateComplaintInfo.productId)?.name || "",
          invoiceNo: updateComplaintInfo.invoiceNo?.toString() || userInvoices.find((i: UserInvoice) => i.orderInvoiceID === updateComplaintInfo.invoiceId)?.invoiceNo || "",
          categoryName: updateComplaintInfo.categoryName || "",
          containerType: containerType || ""
        });

      } catch (error) {
        console.error("Error loading initial data:", error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load initial data',
          life: 3000
        });
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [updateComplaintInfo]);

  const formik = useFormik({
    initialValues: {
      id: updateComplaintInfo.id || 0,
      customerId: updateComplaintInfo.customerId || 0,
      invoiceId: updateComplaintInfo.invoiceId || 0,
      productId: updateComplaintInfo.productId || 0,
      categoryId: updateComplaintInfo.categoryId || 0,
      description: updateComplaintInfo.description || "",
      image: updateComplaintInfo.image || [],
      severity: updateComplaintInfo.severity || "",
      remarks: updateComplaintInfo.remarks || "",
      customerSupportAction: updateComplaintInfo.customerSupportAction || "",
      createdBy: updateComplaintInfo.createdBy || 0,
      createdDate: updateComplaintInfo.createdDate || "",
      modifiedBy: updateComplaintInfo.modifiedBy || 0,
      modifiedDate: updateComplaintInfo.modifiedDate || "",
      userName: updateComplaintInfo.customerName || "",
      userNo: updateComplaintInfo.mobile?.toString() || "",
      productName: updateComplaintInfo.productName || "",
      invoiceNo: updateComplaintInfo.invoiceNo?.toString() || "",
      categoryName: updateComplaintInfo.categoryName || "",
      containerType: containerType || ""
    },
    validationSchema: Yup.object({
      customerId: Yup.number().required("Customer is required"),
      invoiceId: Yup.number().required("Invoice is required"),
      productId: Yup.number().required("Product is required"),
      description: Yup.string().required("Description is required"),
      severity: Yup.string().required("Severity is required"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const complaintData: complaintmodel2 = {
          ...updateComplaintInfo,
          id: values.id,
          customerId: values.customerId,
          invoiceId: values.invoiceId,
          productId: values.productId,
          categoryId: values.categoryId,
          description: values.description,
          image: updateImage,
          severity: values.severity,
          remarks: values.remarks,
          customerSupportAction: values.customerSupportAction,
          modifiedBy: loginUser?.userID || 0,
          modifiedDate: new Date().toISOString(),
          customerName: values.userName,
          mobile: values.userNo,
          productName: values.productName,
          invoiceNo: values.invoiceNo,
          categoryName: values.categoryName,
          containerType: containerType
        };

        const response = await ComplaintService.UpdateProductComplaintInfo(complaintData);
        if (response && response.isSuccess) {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Complaint updated successfully',
            life: 3000
          });
          handleOnSuccess(complaintData);
          window.location.reload();
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: response?.message || 'Failed to update complaint',
            life: 3000
          });
        }
      } catch (error) {
        console.error("Error updating complaint:", error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update complaint',
          life: 3000
        });
      } finally {
        setLoading(false);
      }
    }
  });


  const handleInvoiceChange = async (e: DropdownChangeEvent) => {
    const invoiceId = e.value;
    formik.setFieldValue("invoiceId", invoiceId);
    
    try {
      const invoiceProducts = await ComplaintService.getInvoicedproducts(invoiceId);
      setProducts(invoiceProducts);
      formik.setFieldValue("productId", 0);
    } catch (error) {
      console.error("Error fetching invoice products:", error);
    }
  };

  const handleProductChange = async (e: DropdownChangeEvent) => {
    const productId = e.value;
    formik.setFieldValue("productId", productId);
    
    try {
      const productCategory = await ComplaintService.getProductCategory(productId);
      if (productCategory && productCategory.length > 0) {
        formik.setFieldValue("categoryId", productCategory[0].categoryID);
        formik.setFieldValue("categoryName", productCategory[0].categoryName);
      }
    } catch (error) {
      console.error("Error fetching product category:", error);
    }
  };

  const severityOptions = [
    { label: "Low", value: "Low" },
    { label: "Medium", value: "Medium" },
    { label: "High", value: "High" },
  ];

  if (loading) {
    return <AppLoading />;
  }

  const inputFieldClasses = "w-full p-2 border-round border-1 border-300";
  const textareaClasses = `${inputFieldClasses} resize-none`;
  const dropdownClasses = `${inputFieldClasses} p-dropdown`;

  const handelfileUpload = (query:any) => {
    const file = query;
    setUpdateImage(file)
  }

  const handelfileSizeExceeded = (message:string) => {
    toast.current?.show({
        severity: 'info',
        summary: 'InfoError',
        detail: message,
        life: 3000
    });
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      
      <div className="col-12 flex align-items-stretch flex-wrap">
        <div className="flex flex-1 bg-white align-items-center justify-content-center">
          <form onSubmit={formik.handleSubmit} className="w-full">
            <h5 className="border-round mb-4 p-3 order-top-color p-2 panel-header-text text-center ">
              Update Product Complaint
            </h5>

            <div className="p-fluid formgrid grid">
              <div className="col-12">
                <div className="grid">
                  <div className="col-12 md:col-6 lg:col-4 mb-3 px-3">
                    <div className="field">
                      <label htmlFor="customerId" className="block mb-2 font-bold required-label">
                        Customer <span className="text-red-500">*</span>
                      </label>
                      <InputText
                        id="customerId"
                        name="customerId"
                        value={formik.values.userName || formik.values.customerId.toString()}
                        onChange={formik.handleChange}
                        className={classNames(inputFieldClasses, { 
                          'p-invalid': formik.errors.customerId && formik.touched.customerId 
                        })}
                        placeholder="Enter Customer Name or ID"
                        readOnly
                      />
                      {formik.errors.customerId && formik.touched.customerId && (
                        <small className="p-error block mt-1">{formik.errors.customerId as string}</small>
                      )}
                    </div>
                  </div>

                    <div className="col-12 md:col-6 lg:col-4 mb-3 px-3">
                      <div className="field">
                        <label htmlFor="invoiceId" className="block mb-2 font-bold required-label">
                          Invoice <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                          id="invoiceId"
                          name="invoiceId"
                          options={invoices}
                          optionLabel="invoiceNo"
                          optionValue="orderInvoiceID"
                          value={formik.values.invoiceId}
                          onChange={handleInvoiceChange}
                          className={classNames(dropdownClasses, { 
                            'p-invalid': formik.errors.invoiceId && formik.touched.invoiceId 
                          })}
                          placeholder={
                            invoices.find(i => i.orderInvoiceID === formik.values.invoiceId)?.invoiceNo || 
                            updateComplaintInfo.invoiceNo?.toString() || 
                            "Select Invoice"
                          }
                          disabled
                        />
                        {formik.errors.invoiceId && formik.touched.invoiceId && (
                          <small className="p-error block mt-1">{formik.errors.invoiceId as string}</small>
                        )}
                      </div>
                    </div>

                    <div className="col-12 md:col-6 lg:col-4 mb-3 px-3">
                      <div className="field">
                        <label htmlFor="productId" className="block mb-2 font-bold required-label">
                          Product <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                          id="productId"
                          name="productId"
                          options={products}
                          optionLabel="name"
                          optionValue="productId"
                          value={formik.values.productId}
                          onChange={handleProductChange}
                          disabled
                          className={classNames(dropdownClasses, { 
                            'p-invalid': formik.errors.productId && formik.touched.productId 
                          })}
                          placeholder={
                            products.find(p => p.productId === formik.values.productId)?.name || 
                            "Select Product"
                          }
                        />
                        {formik.errors.productId && formik.touched.productId && (
                          <small className="p-error block mt-1">{formik.errors.productId as string}</small>
                        )}
                      </div>
                    </div>

                    <div className="col-12 md:col-6 lg:col-4 mb-3 px-3">
                      <div className="field">
                        <label htmlFor="categoryId" className="block mb-2 font-bold">
                          Category
                        </label>
                        <InputText
                          id="categoryId"
                          name="categoryId"
                          value={formik.values.categoryName || formik.values.categoryId.toString()}
                          readOnly
                          className={`${inputFieldClasses} bg-gray-100`}
                        />
                      </div>
                    </div>

                    <div className="col-12 mb-3 px-3">
                      <div className="field">
                        <label htmlFor="description" className="block mb-2 font-bold required-label">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <InputTextarea
                          id="description"
                          name="description"
                          rows={3}
                          value={formik.values.description}
                          onChange={(e) => {
                            const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                            formik.setFieldValue('description', cleanedValue);
                            }}
                          className={classNames(textareaClasses, { 
                            'p-invalid': formik.errors.description && formik.touched.description 
                          })}
                        />
                        {formik.errors.description && formik.touched.description && (
                          <small className="p-error block mt-1">{formik.errors.description as string}</small>
                        )}
                      </div>
                    </div>

                    <div className="col-12 md:col-6 lg:col-4 mb-3 px-3">
                      <div className="field">
                        <label htmlFor="severity" className="block mb-2 font-bold required-label">
                          Severity <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                          id="severity"
                          name="severity"
                          options={severityOptions}
                          optionLabel="label"
                          optionValue="value"
                          value={formik.values.severity}
                          onChange={(e) => formik.setFieldValue("severity", e.value)}
                          className={classNames(dropdownClasses, { 
                            'p-invalid': formik.errors.severity && formik.touched.severity 
                          })}
                        />
                        {formik.errors.severity && formik.touched.severity && (
                          <small className="p-error block mt-1">{formik.errors.severity as string}</small>
                        )}
                      </div>
                    </div>

                    <div className="col-12 md:col-6 lg:col-4 mb-3 px-3">
                      <div className="field">
                        <label htmlFor="remarks" className="block mb-2 font-bold">
                          Remarks
                        </label>
                        <InputTextarea
                          id="remarks"
                          name="remarks"
                          rows={3}
                          value={formik.values.remarks}
                          onChange={(e) => {
                            const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                            formik.setFieldValue('remarks', cleanedValue);
                            }}
                          className={textareaClasses}
                        />
                      </div>
                    </div>

                    <div className="col-12 md:col-6 lg:col-4 mb-3 px-3">
                      <div className="field">
                        <label htmlFor="customerSupportAction" className="block mb-2 font-bold">
                          Customer Support Action
                        </label>
                        <InputTextarea
                          id="customerSupportAction"
                          name="customerSupportAction"
                          rows={3}
                          value={formik.values.customerSupportAction}
                          onChange={(e) => {
                            const cleanedValue = e.target.value.replace(/\s\s+/g, ' ');
                            formik.setFieldValue('customerSupportAction', cleanedValue);
                            }}
                          className={textareaClasses}
                        />
                      </div>
                    </div>

                    <div className="col-12 md:col-12 mb-3 px-3">
                      <ImageTemplateDemo onFileUpload={handelfileUpload} handelFileSize={handelfileSizeExceeded}/>
                    </div>
                    <div className="col-12 md:col-12 mb-3 px-3">
                    </div>
                    {formik.values.image?.map((x, index: number) => {
                      const fileUrl = x.fileDetails?.url;
                      const fileType = x.fileDetails?.type?.toLowerCase() || '';
                      const fileName = x.fileDetails?.name || `File-${index + 1}`;

                      const isImage = fileType.startsWith('image');
                      const isPDF = fileType === 'application/pdf';
                      const isVideo = fileType.startsWith('video');
                      const isExcel = fileType.includes('spreadsheet') || fileType.includes('excel');
                      const isWord = fileType.includes('word');
                      const isZip = fileType.includes('zip') || fileType.includes('compressed');

                      const iconClass = isExcel
                        ? 'pi pi-file-excel'
                        : isWord
                        ? 'pi pi-file-word'
                        : isZip
                        ? 'pi pi-file-archive'
                        : 'pi pi-file';

                      return (
                        <div key={index} className="col-12 md:col-6 lg:col-4 mb-3 px-3">
                          <div className="card justify-content-center text-center p-3">

                            {/* ✅ Image Preview */}
                            {isImage && (
                              <>
                                <Image src={fileUrl} alt={fileName} width="250" preview />
                              </>
                            )}

                            {/* ✅ Video Preview */}
                            {isVideo && (
                              <>
                                <video controls width="100%" height="240">
                                  <source src={fileUrl} type="video/mp4" />
                                </video>
                              </>
                            )}

                            {/* ✅ PDF Preview */}
                            {isPDF && (
                              <>
                                <embed
                                  src={fileUrl}
                                  type="application/pdf"
                                  width="100%"
                                  height="300px"
                                  style={{ borderRadius: '6px', border: '1px solid #ccc' }}
                                />
                                <div className="mt-2">{fileName}</div>
                                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="p-button p-button-sm p-button-text mt-2">
                                  Open PDF
                                </a>
                              </>
                            )}

                            {/* ✅ Other File Types */}
                            {!isImage && !isPDF && !isVideo && (
                              <>
                                <i className={iconClass} style={{ fontSize: '3rem', color: '#6b7280' }}></i>
                                <div className="mt-2">{fileName}</div>
                                <a href={fileUrl} download className="p-button p-button-sm p-button-outlined mt-2">
                                  Download
                                </a>
                              </>
                            )}

                          </div>
                        </div>
                      );
                    })}
                    <div className="col-12 mt-4 px-3 flex justify-content-end">
                      <Button 
                        type="submit" 
                        label="Update Complaint" 
                        className="p-button-primary w-full md:w-auto" 
                        disabled={loading}
                        loading={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
    </>
  );
};

export default UpdateProductComplaintDataTable;